import { CheckCircle2, Download, Loader2, RotateCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

import type { ExportStatus } from '../types/export.types';

interface ExportProgressToastProps {
  /** Human context — the experiment / batch name being exported. */
  label: string;
  status: ExportStatus;
  /** 0–100, resolved by the runner from written/expected (or chunk) counts. */
  percent: number;
  writtenProducts: number;
  expectedProducts: number;
  failureReason?: string | null;
  onCancel?: () => void;
  onRetry?: () => void;
  onDownload?: () => void;
  isCancelling?: boolean;
  isRetrying?: boolean;
  isDownloading?: boolean;
  /** True once the user has triggered the download — swaps in a success message and drops the Download button. */
  downloaded?: boolean;
  /**
   * Every file of a multi-file (transformed) export. With two or more, the toast
   * lists them with a Download button each instead of the single Download.
   */
  files?: { name: string; downloaded: boolean }[];
  onDownloadFile?: (index: number) => void;
  /** The file layout a transformed export ran ("Hibbett · Catalog XML v3"); null on the standard path. */
  layoutName?: string | null;
}

/** Status glyph: spinner while running, check on done, cross on failure/cancel. */
function StatusIcon({ status }: { status: ExportStatus }) {
  if (status === 'COMPLETED') {
    return <CheckCircle2 aria-hidden="true" className="text-success h-4 w-4 shrink-0" />;
  }
  if (status === 'FAILED' || status === 'CANCELLED') {
    return <XCircle aria-hidden="true" className="text-error h-4 w-4 shrink-0" />;
  }
  return <Loader2 aria-hidden="true" className="text-primary h-4 w-4 shrink-0 animate-spin" />;
}

/** Inline progress bar — same track/fill tokens as the analytics-sync toast. */
function MiniBar({ percent }: { percent: number }) {
  const pct = Math.max(0, Math.min(100, percent));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className="h-1.5 w-full overflow-hidden rounded-full"
      style={{ backgroundColor: 'var(--surface-progress-track)' }}
    >
      <div
        className="bg-primary h-full rounded-full transition-[width] duration-300 ease-out"
        style={{ width: `${String(pct)}%` }}
      />
    </div>
  );
}

/**
 * Non-blocking progress toast body for a CSV export. The app-level runner
 * re-emits this with the same toast id on every SSE frame so it updates in
 * place; on terminal states it surfaces Download (COMPLETED) or Retry (FAILED).
 */
export function ExportProgressToast({
  label,
  status,
  percent,
  writtenProducts,
  expectedProducts,
  failureReason,
  onCancel,
  onRetry,
  onDownload,
  isCancelling,
  isRetrying,
  isDownloading,
  downloaded,
  files,
  onDownloadFile,
  layoutName,
}: ExportProgressToastProps) {
  const { t } = useTranslation('exports');
  const { t: tl } = useTranslation('transformers');
  const isRunning = status === 'PENDING' || status === 'IN_PROGRESS';
  // A transformed export that wrote several files offers one Download per file.
  const fileList = status === 'COMPLETED' && files && files.length > 1 ? files : null;
  // Once the download has fired, the COMPLETED toast becomes a success notice
  // (no browser event tells us it finished writing, so this reflects "started").
  const isDownloaded = status === 'COMPLETED' && (downloaded ?? false);

  const heading = isDownloaded
    ? t('toast.title.downloaded')
    : status === 'COMPLETED'
      ? t('toast.title.completed')
      : status === 'FAILED'
        ? t('toast.title.failed')
        : status === 'CANCELLED'
          ? t('toast.title.cancelled')
          : t('toast.title.running');

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-foreground flex items-center gap-1.5 text-sm font-semibold">
          <StatusIcon status={status} />
          {heading}
        </span>
        {isRunning && (
          <span className="text-primary text-xs font-semibold">{Math.round(percent)}%</span>
        )}
      </div>

      <p className="text-muted-foreground truncate text-xs">{label}</p>

      {/* A transformed export names its layout and, when it wrote one file, that
          file (several files are listed with their own Download buttons below). */}
      {status === 'COMPLETED' && layoutName && (
        <p className="text-muted-foreground truncate text-xs" title={layoutName}>
          {tl('layout.toastLayout', { name: layoutName })}
        </p>
      )}
      {status === 'COMPLETED' && layoutName && files?.length === 1 && files[0] && (
        <p className="text-foreground truncate font-mono text-xs" title={files[0].name}>
          {files[0].name}
        </p>
      )}

      {isRunning && (
        <>
          <MiniBar percent={percent} />
          {expectedProducts > 0 && (
            <p className="text-muted-foreground text-xs">
              {t('toast.progressCount', {
                written: writtenProducts,
                total: expectedProducts,
              })}
            </p>
          )}
        </>
      )}

      {status === 'FAILED' && failureReason && (
        <p className="text-error text-xs">{failureReason}</p>
      )}

      {/* Post-download hint — the browser can't open the downloads folder, so
          we just point the user to where the file landed. */}
      {isDownloaded && <p className="text-muted-foreground text-xs">{t('toast.downloadedHint')}</p>}

      {fileList && !isDownloaded && (
        <ul className="flex flex-col gap-1">
          {fileList.map((file, index) => (
            <li key={file.name} className="flex items-center justify-between gap-2">
              <span className="text-foreground min-w-0 truncate text-xs" title={file.name}>
                {file.name}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onDownloadFile?.(index)}
                isLoading={isDownloading ?? false}
                aria-label={`${t('toast.actions.download')} ${file.name}`}
                className="shrink-0 cursor-pointer"
              >
                {file.downloaded ? (
                  <CheckCircle2 aria-hidden="true" className="text-success h-3.5 w-3.5" />
                ) : (
                  <Download aria-hidden="true" className="h-3.5 w-3.5" />
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {(isRunning ||
        (status === 'COMPLETED' && !isDownloaded && !fileList) ||
        status === 'FAILED') && (
        <div className="mt-1 flex items-center justify-end gap-2">
          {isRunning && onCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              isLoading={isCancelling ?? false}
              className="cursor-pointer"
            >
              {t('toast.actions.cancel')}
            </Button>
          )}
          {status === 'FAILED' && onRetry && (
            <Button
              type="button"
              size="sm"
              onClick={onRetry}
              isLoading={isRetrying ?? false}
              className="cursor-pointer"
            >
              <RotateCw aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
              {t('toast.actions.retry')}
            </Button>
          )}
          {status === 'COMPLETED' && !isDownloaded && !fileList && onDownload && (
            <Button
              type="button"
              size="sm"
              onClick={onDownload}
              isLoading={isDownloading ?? false}
              className="cursor-pointer"
            >
              <Download aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
              {t('toast.actions.download')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
